/* eslint-disable react-hooks/rules-of-hooks */
import { useToast } from '@chakra-ui/react';
import { PatchDataWrapper } from '@diamond/shared/data-access';
import {
  BackofficeActivitiesOrderItem,
  BackofficeOrderDetail,
  BackofficeOrderUpdate,
  BackofficeRevalidateUpdate,
} from '@diamond/shared/types';
import { QueryKey } from '@tanstack/react-query';
import { CellContext, createColumnHelper, Row } from '@tanstack/react-table';
import { useMemo } from 'react';

import { useActivityDetail } from '../../hooks/use-activity-detail';
import {
  CancelReasonColumn,
  CheckboxColumn,
  CheckboxHeader,
  FulfillmentPriceColumn,
  IsUpdatedColumn,
  ItemCodeColumn,
  NoteColumn,
  PriceColumn,
  ProductColumn,
  QuantityColumn,
  StageSubtotalColumn,
  StorageTypeColumn,
  ValuationTypeColumn,
} from '../activity-details-columns';
import { isExpandedOnlyColumn } from './activity-detail-column-layout';
import { ActivityDetailStageQtyCell } from './activity-detail-stage-qty-cell';

const columnHelper = createColumnHelper<BackofficeActivitiesOrderItem>();
const API_URL = 'backoffice/order';

type UseActivityDetailGroupedColumnsParams = {
  setSelectedData: (value: Row<BackofficeActivitiesOrderItem>[]) => void;
  selectedData: Row<BackofficeActivitiesOrderItem>[];
  refetch: () => void;
  activitiesId: string;
  cancelReasonData: { label: string; value: string }[];
  isDisable: boolean;
  queryKey?: QueryKey;
  token?: string;
  toast?: ReturnType<typeof useToast>;
  isVoucherEnabled?: boolean;
  isExpanded?: boolean;
  orderStatus?: BackofficeOrderDetail['order']['status'];
};

export function useActivityDetailGroupedColumns({
  setSelectedData,
  selectedData,
  refetch,
  activitiesId,
  cancelReasonData,
  isDisable,
  queryKey = [],
  token,
  toast,
  isVoucherEnabled = false,
  isExpanded = false,
  orderStatus,
}: UseActivityDetailGroupedColumnsParams) {
  const activityDetail = useActivityDetail({
    selectedData,
    setSelectedData,
    refetch,
    activitiesId,
    queryKey,
    isVoucherEnabled,
    token,
    toast,
  });

  const handleSelectAll = (
    values: Row<BackofficeActivitiesOrderItem>[],
    isChecked: boolean
  ) => {
    activityDetail.handleSelectAll(values, isChecked);
  };

  const handleSelectOne = (
    value: Row<BackofficeActivitiesOrderItem>,
    isChecked: boolean
  ) => {
    activityDetail.handleSelectOneRow(value, isChecked);
  };

  const handleUpdate = (name: string, value: unknown, itemId: string) => {
    const { patchData } = PatchDataWrapper<
      BackofficeOrderUpdate & BackofficeRevalidateUpdate
    >(API_URL, token as string, 'admin');

    patchData({ [name]: value }, '', `${activitiesId}/item/${itemId}`).then(
      () => {
        refetch();
      }
    );
  };

  const sharedEditProps = {
    selectedData,
    queryKey,
    activitiesId,
    token,
    refetch,
    onUpdate: handleUpdate,
  };

  return useMemo(() => {
    const allColumns = [
      {
        id: 'select',
        header: ({
          table,
        }: {
          table: {
            getRowModel: () => { rows: Row<BackofficeActivitiesOrderItem>[] };
          };
        }) => (
          <CheckboxHeader
            table={table}
            onSelectAll={handleSelectAll}
            isDisabled={isDisable}
          />
        ),
        cell: ({ row }: { row: Row<BackofficeActivitiesOrderItem> }) => (
          <CheckboxColumn
            info={row}
            selectedData={selectedData}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            isDisabled={isDisable}
          />
        ),
      },
      columnHelper.accessor('item_code', {
        id: 'item_code',
        header: '',
        cell: (info) => <ItemCodeColumn info={info} />,
      }),
      columnHelper.accessor('title', {
        id: 'product',
        header: '',
        cell: (info) => <ProductColumn info={info} />,
      }),
      columnHelper.accessor('note', {
        id: 'note',
        header: '',
        cell: (info) => <NoteColumn info={info} {...sharedEditProps} />,
      }),
      columnHelper.accessor('valuation_type', {
        id: 'valuation_type',
        header: '',
        cell: (info) => (
          <ValuationTypeColumn info={info} {...sharedEditProps} />
        ),
      }),
      columnHelper.accessor('quantity', {
        id: 'checkout_qty',
        header: '',
        cell: (info) => (
          <QuantityColumn
            info={info}
            variant="checkout"
            orderStatus={orderStatus}
          />
        ),
      }),
      columnHelper.accessor('price', {
        id: 'checkout_harga',
        header: '',
        cell: (info) => (
          <PriceColumn
            info={info}
            isVoucherEnabled={isVoucherEnabled}
            align="center"
            orderStatus={orderStatus}
          />
        ),
      }),
      columnHelper.accessor('subtotal', {
        id: 'checkout_subtotal',
        header: '',
        cell: (info) => (
          <StageSubtotalColumn
            info={info}
            mode="checkout"
            orderStatus={orderStatus}
          />
        ),
      }),
      columnHelper.accessor('processed_quantity', {
        id: 'so_qty',
        header: '',
        cell: (info) => (
          <ActivityDetailStageQtyCell
            info={info}
            value={info.getValue()}
            stage="processed"
            orderStatus={orderStatus}
          />
        ),
      }),
      columnHelper.accessor('latest_price', {
        id: 'so_harga',
        header: '',
        cell: (info) => (
          <FulfillmentPriceColumn
            info={info}
            stage="processed"
            editable
            isVoucherEnabled={isVoucherEnabled}
            orderStatus={orderStatus}
            {...sharedEditProps}
          />
        ),
      }),
      columnHelper.accessor('processed_subtotal', {
        id: 'so_subtotal',
        header: '',
        cell: (info) => (
          <StageSubtotalColumn
            info={info}
            mode="processed_subtotal"
            orderStatus={orderStatus}
          />
        ),
      }),
      columnHelper.accessor('delivered_quantity', {
        id: 'pgi_qty',
        header: '',
        cell: (info) => (
          <ActivityDetailStageQtyCell
            info={info}
            value={info.getValue()}
            stage="delivered"
            orderStatus={orderStatus}
          />
        ),
      }),
      columnHelper.accessor('latest_price', {
        id: 'pgi_harga',
        header: '',
        cell: (info) => (
          <FulfillmentPriceColumn
            info={info}
            stage="delivered"
            isVoucherEnabled={isVoucherEnabled}
            orderStatus={orderStatus}
            {...sharedEditProps}
          />
        ),
      }),
      columnHelper.accessor('delivered_subtotal', {
        id: 'pgi_subtotal',
        header: '',
        cell: (info) => (
          <StageSubtotalColumn
            info={info}
            mode="delivered_subtotal"
            orderStatus={orderStatus}
          />
        ),
      }),
      columnHelper.accessor('received_quantity', {
        id: 'pod_qty',
        header: '',
        cell: (info) => (
          <ActivityDetailStageQtyCell
            info={info}
            value={info.getValue()}
            stage="received"
            orderStatus={orderStatus}
          />
        ),
      }),
      columnHelper.accessor('latest_price', {
        id: 'pod_harga',
        header: '',
        cell: (info) => (
          <FulfillmentPriceColumn
            info={info}
            stage="received"
            isVoucherEnabled={isVoucherEnabled}
            orderStatus={orderStatus}
            {...sharedEditProps}
          />
        ),
      }),
      columnHelper.accessor('received_subtotal', {
        id: 'pod_subtotal',
        header: '',
        cell: (info) => (
          <StageSubtotalColumn
            info={info}
            mode="received_subtotal"
            orderStatus={orderStatus}
          />
        ),
      }),
      columnHelper.accessor('product_storage', {
        id: 'product_storage',
        header: '',
        cell: (info) => <StorageTypeColumn info={info} {...sharedEditProps} />,
      }),
      columnHelper.accessor('is_updated', {
        id: 'is_updated',
        header: '',
        cell: (info) => <IsUpdatedColumn info={info} />,
      }),
      columnHelper.accessor('cancel_reason_id', {
        id: 'cancel_reason_id',
        header: '',
        cell: (info) => (
          <CancelReasonColumn
            info={
              info as CellContext<
                BackofficeActivitiesOrderItem,
                string | null | undefined
              >
            }
            {...sharedEditProps}
            cancelReasonData={cancelReasonData}
          />
        ),
      }),
    ];

    if (isExpanded) {
      return allColumns;
    }

    return allColumns.filter(
      (column) => !isExpandedOnlyColumn(String(column.id))
    );
  }, [
    activitiesId,
    cancelReasonData,
    handleSelectAll,
    handleSelectOne,
    isDisable,
    isVoucherEnabled,
    queryKey,
    refetch,
    selectedData,
    token,
    isExpanded,
    orderStatus,
  ]);
}
